import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Sparkle, Robot, Warning } from "@phosphor-icons/react";
import { SeverityBadge } from "@/components/SeverityBadge";
import { toast } from "sonner";

export default function AiAnalysisPanel({ threat, onAnalyzed }) {
  const [busy, setBusy] = useState(false);
  const [analysis, setAnalysis] = useState(threat?.ai_analysis || "");

  const run = async () => {
    if (!threat) return;
    setBusy(true);
    try {
      const { data } = await api.post("/threats/analyze", { threat_id: threat.id });
      setAnalysis(data.analysis);
      onAnalyzed && onAnalyzed(threat.id, data.analysis);
      toast.success("AI analysis complete");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Analysis failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="bg-[#12121E] border border-[#00F0FF]/30 rounded-none p-5 relative overflow-hidden"
      style={{ boxShadow: "0 0 40px -20px rgba(0,240,255,0.4)" }}
      data-testid="ai-analysis-panel"
    >
      <div className="flex items-center gap-2 mb-1">
        <Sparkle size={16} weight="fill" color="#00F0FF" />
        <span className="text-[10px] uppercase tracking-[0.25em] text-[#00F0FF]">AI Threat Intelligence</span>
      </div>

      {!threat ? (
        <div className="py-10 text-center text-xs text-[#71717A] flex flex-col items-center gap-3">
          <Robot size={32} weight="duotone" color="#33334D" />
          Select a threat from the feed to generate an AI analysis.
        </div>
      ) : (
        <>
          <h3 className="font-head text-lg tracking-tight font-bold mt-2 mb-2">{threat.title}</h3>
          <div className="flex items-center gap-3 mb-4 text-[11px] text-[#71717A]">
            <SeverityBadge level={threat.severity} />
            <span>{threat.category}</span>
            <span>→ {threat.target}</span>
          </div>

          {analysis ? (
            <pre
              data-testid="ai-analysis-result"
              className="whitespace-pre-wrap text-xs leading-relaxed text-[#A1A1AA] font-mono border-t border-[#1F1F33] pt-4"
            >
              {analysis}
            </pre>
          ) : (
            <div className="text-xs text-[#71717A] border border-dashed border-[#1F1F33] p-4 flex items-center gap-2">
              <Warning size={16} color="#FFCC00" />
              No analysis yet for this threat.
            </div>
          )}

          <button
            data-testid="run-ai-analysis-button"
            onClick={run}
            disabled={busy}
            className="w-full mt-4 flex items-center justify-center gap-2 bg-[#00F0FF] text-black font-bold uppercase tracking-[0.15em] text-[11px] py-2.5 rounded-none hover:bg-white active:scale-[0.98] transition-all duration-150 disabled:opacity-50"
          >
            <Sparkle size={14} weight="fill" />
            {busy ? "Analyzing…" : analysis ? "Re-run Analysis" : "Run AI Analysis"}
          </button>
        </>
      )}
    </div>
  );
}
